/**
 * MapLibre style generation for MapWall posters.
 *
 * The posters used to show a fixed OpenStreetMap raster recoloured after the
 * fact, which meant every style was the same map with a filter over it. Vector
 * tiles let each poster style set water, land, roads and labels directly, the
 * way a real map poster is designed.
 *
 * Tiles come from OpenFreeMap: free, no key, no quota, and the same source the
 * Rijassistent app uses. Attribution is required and rendered by the map.
 */

import type { MapWallStyleId } from './mapwall';

const OPENFREEMAP_TILES = 'https://tiles.openfreemap.org/planet';

export type MapWallPalette = {
  /** Everything that is not water or a named feature. */
  land: string;
  water: string;
  /** Parks, forest, greenery. */
  green: string;
  /** Motorways and primary roads: the strongest lines on the poster. */
  majorRoad: string;
  /** Everything smaller, drawn thinner. */
  minorRoad: string;
  building: string;
  label: string;
  labelHalo: string;
  /** Whether place labels are drawn at all. A clean poster often reads better. */
  showLabels: boolean;
};

export const mapWallPalettes: Record<MapWallStyleId, MapWallPalette> = {
  minimal: {
    land: '#ffffff',
    water: '#e2e8f0',
    green: '#f1f5f9',
    majorRoad: '#111827',
    minorRoad: '#cbd5e1',
    building: '#eef2f7',
    label: '#0f172a',
    labelHalo: '#ffffff',
    showLabels: true,
  },
  warm: {
    land: '#fdf6ec',
    water: '#e7d6be',
    green: '#f2e7d5',
    majorRoad: '#8a6a4b',
    minorRoad: '#dcc9ae',
    building: '#f6ecdd',
    label: '#4a3524',
    labelHalo: '#fdf6ec',
    showLabels: true,
  },
  coastal: {
    land: '#f8fdff',
    water: '#8ecae6',
    green: '#e4f4ea',
    majorRoad: '#1f6f8b',
    minorRoad: '#bfdde8',
    building: '#eaf6fb',
    label: '#0f2f3f',
    labelHalo: '#f8fdff',
    showLabels: true,
  },
  atlas: {
    land: '#fdf8ea',
    water: '#bcd4c8',
    green: '#e3e8cd',
    majorRoad: '#7d6a3f',
    minorRoad: '#ded3ae',
    building: '#f3ecd7',
    label: '#3a3a24',
    labelHalo: '#fdf8ea',
    showLabels: true,
  },
  dark: {
    land: '#0b1017',
    water: '#16222e',
    green: '#111b18',
    majorRoad: '#e8eef6',
    minorRoad: '#31404f',
    building: '#141c25',
    label: '#e8eef6',
    labelHalo: '#0b1017',
    showLabels: true,
  },
};

/**
 * Build a complete MapLibre style for one poster style.
 *
 * Written out rather than patching a hosted style, so the poster looks identical
 * whatever OpenFreeMap changes upstream, and so the layer set stays small enough
 * to render fast in the editor.
 */
export function buildMapWallMapStyle(styleId: MapWallStyleId): any {
  const palette = mapWallPalettes[styleId] || mapWallPalettes.minimal;

  const layers: any[] = [
    { id: 'background', type: 'background', paint: { 'background-color': palette.land } },
    {
      id: 'landuse-green',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'landcover',
      filter: ['in', 'class', 'wood', 'grass', 'scrub', 'forest', 'park'],
      paint: { 'fill-color': palette.green },
    },
    {
      id: 'park',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'park',
      paint: { 'fill-color': palette.green, 'fill-opacity': 0.7 },
    },
    {
      id: 'water',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'water',
      paint: { 'fill-color': palette.water },
    },
    {
      id: 'waterway',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'waterway',
      paint: {
        'line-color': palette.water,
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.6, 14, 2.4],
      },
    },
    {
      id: 'building',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'building',
      minzoom: 13,
      paint: { 'fill-color': palette.building },
    },
    {
      id: 'road-minor',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['!in', 'class', 'motorway', 'trunk', 'primary'],
      paint: {
        'line-color': palette.minorRoad,
        'line-width': ['interpolate', ['linear'], ['zoom'], 9, 0.25, 14, 1.4, 17, 4],
      },
    },
    {
      id: 'road-major',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['in', 'class', 'motorway', 'trunk', 'primary'],
      paint: {
        'line-color': palette.majorRoad,
        'line-width': ['interpolate', ['linear'], ['zoom'], 6, 0.5, 11, 1.6, 15, 4.5],
      },
    },
    {
      id: 'boundary',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'boundary',
      filter: ['<=', 'admin_level', 4],
      paint: { 'line-color': palette.minorRoad, 'line-width': 0.8, 'line-dasharray': [3, 2] },
    },
  ];

  if (palette.showLabels) {
    layers.push({
      id: 'place-label',
      type: 'symbol',
      source: 'openmaptiles',
      'source-layer': 'place',
      filter: ['in', 'class', 'city', 'town', 'village'],
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Regular'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 6, 10, 12, 15],
        'text-max-width': 8,
      },
      paint: {
        'text-color': palette.label,
        'text-halo-color': palette.labelHalo,
        'text-halo-width': 1.4,
      },
    });
  }

  return {
    version: 8,
    glyphs: 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf',
    sources: {
      openmaptiles: {
        type: 'vector',
        url: OPENFREEMAP_TILES,
        attribution: '&copy; OpenStreetMap contributors',
      },
    },
    layers,
  };
}
